<template>
  <mf-card-container
    v-if="!isCashbackOrPointsCurrentOfferType"
    v-model="search"
    title="Histórico de consultas de desconto"
    description="Seu PDV consulta os descontos disponíveis para cada CPF na plataforma Mercafacil. É isso que estamos exibindo aqui. Depois da atualização da sua plataforma teremos a confirmação da venda."
    useSearch
    placeholder="Buscar por código ou consumidor"
  >
    <template v-slot:filters>
      <export-email-report-dialog
        ref="exportEmailReportDialog"
        title="Enviar relatório de histórico de consultas de desconto"
        description="O relatório da lista completa de  histórico de consultas de desconto é enviado, através de um e-mail, para a caixa de entrada dos endereços de e-mail adicionados abaixo. Um arquivo CSV contendo todas as informações detalhadas da lista completa de histórico de consultas é enviado em anexo ao e-mail."
        :exportReportFn="exportConsumedSalesHistory"
      />
      <filters-menu :filters="['lojas', 'couponStatus']" default-date="desdeInicio" hide-ecommerce-configuration />
    </template>

    <paginated-data-table
      ref="table"
      :headers="headers"
      :search="search"
      :query="query"
      :custom-items-per-page="10"
      :query-variables="queryVariables"
      :max-records.sync="maxRecords"
      :skip="skip"
      query-name="consumedSales"
    >
      <template v-slot:header.consumed_at="{ header }">
        {{ header.text }}

        <v-tooltip bottom max-width="192px">
          <template v-slot:activator="{ on }">
            <v-icon small color="grey" v-on="on">info_outline</v-icon>
          </template>
          <span>Representa o momento em que seu PDV nos enviou a confirmação de consumo dessa oferta</span>
        </v-tooltip>
      </template>
      <template v-slot:item="{ item }">
        <tr>
          <td>{{ getFormatedDate(DateTime.fromISO(item.consumed_at)) }}</td>
          <td>
            <a class="link" target="_blank" :href="linkProduct(item.product.sku)">{{ item.product.sku }}</a>
          </td>
          <td class="nowrap-row">
            <v-tooltip max-width="250" top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ putEllipsis(item.product.name, 18) }}</span>
              </template>
              <span>{{ item.product.name }}</span>
            </v-tooltip>
          </td>
          <td v-if="item.processed_coupon_status === 'RECOVERED'">
            <v-tooltip max-width="250" top>
              <template v-slot:activator="{ on }">
                <span class="link" @click="openCoupon(item)" v-on="on">{{ putEllipsis(item.processed_coupon_id, 18) }}</span>
              </template>
              <span>Clique para ver o cupom.</span>
            </v-tooltip>
          </td>
          <td v-else>
            <v-tooltip max-width="250" top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ item.processed_coupon_status === 'NOT_RECOVERED' ? 'Não recuperado' : 'Ainda não gerado' }}</span>
              </template>
              <span>{{ getCouponStatusTooltip(item.processed_coupon_status) }}</span>
            </v-tooltip>
          </td>
          <td class="nowrap-row">
            <v-tooltip max-width="250" top>
              <template v-slot:activator="{ on }">
                <span v-on="on">{{ putEllipsis(item.unit.name, 18) }}</span>
              </template>
              <span>{{ item.unit.name }}</span>
            </v-tooltip>
          </td>
          <td class="nowrap-row">
            <template v-if="item.user">
              <a v-if="hasPiiAccess" target="_blank" class="link" :href="linkConsumer(item.user.document_no)">{{ item.user.document_no }}</a>
              <span v-else>{{ item.user.document_no }}</span>
            </template>
            <template v-else>
              <span>-</span>
            </template>
          </td>
          <td>{{ item.consumed_sale | localeString(0) }}</td>
        </tr>
      </template>
    </paginated-data-table>
    <coupon-modal v-model="couponModal.showCouponModal" :coupon="couponModal.selectedCoupon" :client-id="couponModal.clientId" />
  </mf-card-container>
  <div v-else class="cashback-message">
    <h1 class="section-title">Sessão de acompanhamento</h1>
    <p class="texto-corrido">{{ cashbackOrPointsDescription }}</p>
  </div>
</template>

<script>
import PaginatedDataTable from '@/components/PaginatedDataTable.vue'
import CouponModal from '@/modules/clientes/detalhes-cliente/views/components/CouponModal.vue'
import ExportEmailReportDialog from '@/components/export-email-report/ExportEmailReportDialog.vue'

import { putEllipsis } from '@/config/helpers.js'
import { QUERY_CONSUMED_SALES, MUTATION_EXPORT_CONSUMED_SALES_REPORT } from '../../graphql'
import { DateTime } from 'luxon'
import { mapState } from 'vuex'
import { getOfferTypeName } from '@/modules/campanhas/gestao-ofertas-v2/helpers/offerType.js'

export default {
  name: 'ConsumedSalesTableHistory',
  components: {
    PaginatedDataTable,
    CouponModal,
    ExportEmailReportDialog,
    FiltersMenu: () => import('@/components/FiltersMenu.vue'),
  },
  props: {
    campanha: {
      type: Object,
      required: true,
    },
    currentOfferTypeKeyName: {
      type: String,
      default: 'offer_sales',
    },
  },
  data: () => ({
    loading: true,
    search: '',
    maxRecords: 0,
    DateTime: DateTime,
    couponModal: {
      showCouponModal: false,
      selectedCoupon: {},
      clientId: '',
    },
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    skip() {
      return this.stateFilters.units < 1 || this.stateFilters.unitsConfiguration < 1
    },
    query() {
      return QUERY_CONSUMED_SALES
    },
    queryVariables() {
      const currentType = getOfferTypeName(this.currentOfferTypeKeyName)
      const type = [currentType === 'dynamic_pricing_percentage' ? 'general' : currentType]
      return {
        id: this.campanha._id,
        units: this.stateFilters.units,
        units_configuration: this.stateFilters.unitsConfiguration,
        status: this.stateFilters.couponStatus,
        type,
      }
    },
    headers() {
      return [
        {
          text: 'Hora da consulta',
          value: 'consumed_at',
          width: '13%',
        },
        {
          text: 'Código',
          value: 'sku',
          width: '10%',
        },
        {
          text: 'Produto',
          value: 'product.name',
          width: '13%',
        },
        {
          text: 'ID cupom',
          value: 'product.coupon_id',
          width: '13%',
        },
        {
          text: 'Loja',
          value: 'unit.name',
          width: '10.5%',
        },
        {
          text: 'Consumidor',
          value: 'user.document_no',
          width: '10%',
        },
        {
          text: 'Qtd com desconto',
          value: 'consumed_sale',
          width: '11%',
        },
      ]
    },
    isCashbackCurrentOfferType() {
      return ['cashback_absolute_offer_sales', 'cashback_percentage_offer_sales'].includes(this.currentOfferTypeKeyName)
    },
    isCashbackOrPointsCurrentOfferType() {
      return this.isCashbackCurrentOfferType || this.currentOfferTypeKeyName === 'points_offer_sales'
    },
    cashbackOrPointsDescription() {
      if (this.isCashbackCurrentOfferType)
        return 'O Cashback é gerado junto com o processamento noturno que atualiza a sua plataforma. Dessa maneira, nenhuma informação pode ser gerada em tempo real.'
      return 'Os Pontos são gerados junto com o processamento noturno que atualiza a sua plataforma. Dessa maneira, nenhuma informação pode ser gerada em tempo real.'
    },
  },
  methods: {
    putEllipsis,
    getFormatedCouponStatus(cupomStatus, cupomId) {
      const cupomStatusLabel = {
        RECOVERED: cupomId,
        NOT_RECOVERED: 'Não recuperado',
        NOT_PROCESSED: 'Ainda não gerado',
      }
      return cupomStatusLabel[cupomStatus]
    },
    getFormatedDate(date) {
      const now = DateTime.now().toFormat('dd/MM/yyyy')
      const currentDate = DateTime.fromISO(date).toFormat('dd/MM/yyyy')
      const formatedDate = DateTime.fromISO(date).toRelative()

      if (now === currentDate) return formatedDate
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy - HH:mm')
    },
    getCouponStatusTooltip(status) {
      if (status === 'NOT_RECOVERED')
        return 'Isso acontece quando a venda é cancelada antes do fechamento do PDV, ou quando o retaguarda não retornou pra gente esse cupom.'
      return 'O ID do cupom é gerado no fechamento do seu PDV, e é computado no processamento da sua plataforma. O que ainda não aconteceu.'
    },
    openCoupon(item) {
      this.couponModal = {
        showCouponModal: true,
        selectedCoupon: { id: item.processed_coupon_id },
        clientId: item.user.document_no,
      }
    },
    async exportConsumedSalesHistory(emails) {
      const currentType = getOfferTypeName(this.currentOfferTypeKeyName).toUpperCase()
      const salesType = [currentType === 'DYNAMIC_PRICING_PERCENTAGE' ? 'GENERAL' : currentType]

      return this.$apollo.mutate({
        mutation: MUTATION_EXPORT_CONSUMED_SALES_REPORT,
        fetchPolicy: 'no-cache',
        variables: {
          emails,
          offersScheduleId: this.campanha._id,
          salesType,
          units: this.stateFilters.units,
          unitsConfiguration: this.stateFilters.unitsConfiguration,
        },
        context: {
          uri: this.$microservicesUrls['asyncJobs'],
        },
      })
    },
    linkProduct(param) {
      return `#/produtos/detalhes-produto/${param}`
    },
    linkConsumer(param) {
      return `#/clientes/detalhes-cliente/${param}`
    },
  },
}
</script>
<style lang="scss">
.link {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 150%;
  letter-spacing: 0.01em;
  color: $marca-0-l40 !important;
  text-transform: none;
  text-decoration-line: none;
  &:hover {
    text-decoration-line: underline;
  }
}
.cashback-message {
  background: $cinza-l80;
  padding: 32px;
  border-radius: $border-radius-rounded;

  p {
    max-width: 660px;
  }
}
</style>
